import { useCallback, useMemo, useState } from "react";

import { addDays, isFuture } from "date-fns";
import { useSelector } from "react-redux";

import { useObjectMemo } from "../../../hooks";
import { useFeature } from "../features";
import filters from "../../../../filters";
import {
  selectBAccount,
  selectMinPaymentDueDate,
  selectMinimumPayment,
} from "../../entities/accounts/selectors";
import { isLoan, useAccount } from "../../entities/accounts";
import { selectInstitution } from "../../entities/selectors/institution.selectors";
import { maskAccountNumber } from "../../../utils/masks";

import { loanPaymentForm } from "./LoanPayment.form";
import { useGetLoanAutopay } from "./requests";
import { canEditAutopay } from "./utils";

import type { LoanAutopayData } from "./types";
import type { RootState } from "../../entities/store";

export const useLoanAutopay = (
  accountId?: API.AccountId,
): {
  isLoanAutopayEnabled: boolean;
  isLoading: boolean;
  autopayData: LoanAutopayData | null;
} => {
  const { featureEnabled } = useFeature();
  const isLoanAutopayEnabled = featureEnabled("olb_loan_autopay");
  const { data, isLoading } = useGetLoanAutopay(
    // when mounted on the Loan payment form, the loan may not have been selected yet
    accountId || "",
    isLoanAutopayEnabled,
  );
  const enrolledAutopay = data?.loan_autopay.is_enrolled
    ? data.loan_autopay
    : null;

  const internalSourceAccountId =
    enrolledAutopay?.source_account_origin === "internal"
      ? (enrolledAutopay.source_account_id as API.AccountId)
      : undefined;
  const sourceAccount = useAccount(internalSourceAccountId);

  const autopayData: LoanAutopayData | null = useMemo(() => {
    if (!enrolledAutopay) {
      return null;
    }

    let sourceAccountName = "";
    if (enrolledAutopay.source_account_origin === "external") {
      sourceAccountName = `**${maskAccountNumber(enrolledAutopay.source_account_number)}`;
    } else if (sourceAccount) {
      sourceAccountName = sourceAccount.getShortDescription();
    }

    return {
      sourceAccountName,
      sourceAccountNumber: enrolledAutopay.source_account_number,
      sourceAccountOrigin: enrolledAutopay.source_account_origin,
      sourceAccountId:
        enrolledAutopay.source_account_id as API.AccountId | null,
      paymentAmount: enrolledAutopay.amount
        ? filters.currency(enrolledAutopay.amount)
        : null,
      paymentDate: filters.shortMonthDayYear(
        enrolledAutopay.next_scheduled_date,
      ),
      canEdit: canEditAutopay(enrolledAutopay.next_scheduled_date),
    };
  }, [enrolledAutopay, sourceAccount]);

  return useObjectMemo({
    isLoading,
    isLoanAutopayEnabled,
    autopayData,
  });
};

export const useLoanAutopayManageForm = (
  currentSourceAccountId?: API.AccountId,
) => {
  const [selectedSourceAccountId, setSelectedSourceAccountId] = useState<
    API.AccountId | undefined
  >(currentSourceAccountId);
  const [showError, setShowError] = useState(false);

  const selectedAccount = useAccount(selectedSourceAccountId);
  const sourceAccountName = selectedAccount?.getName() || "";
  const sourceAccountNumberLastFour = selectedAccount?.getMaskedNumber() || "";

  const accountChangeHandler = useCallback(
    (id: string) => {
      if (showError) {
        setShowError(false);
      }
      setSelectedSourceAccountId(id as API.AccountId);
    },
    [showError],
  );

  const submitHandler = useCallback(
    (successCallback: () => void) => {
      if (!selectedSourceAccountId) {
        setShowError(true);
      } else {
        successCallback();
      }
    },
    [selectedSourceAccountId],
  );

  return useObjectMemo({
    accountChangeHandler,
    submitHandler,
    showError,
    selectedSourceAccountId,
    sourceAccountName,
    sourceAccountNumberLastFour,
  });
};

export const useLoanPaymentEnabled = () => {
  const { featureEnabled } = useFeature();
  const hasTransferAccess = useMemo(
    () =>
      featureEnabled({
        or: ["ach", "internal_transfers"],
      }),
    [featureEnabled],
  );

  return useObjectMemo({
    isMobileLoanPaymentsEnabled:
      hasTransferAccess && featureEnabled("enable_loan_payments_mobile"),
    isWebLoanPaymentsEnabled:
      hasTransferAccess && featureEnabled("enable_loan_payments_web"),
    isPrincipalPaymentEnabled:
      hasTransferAccess && featureEnabled("loan_principal_payment"),
    isCustomPaymentEnabled:
      hasTransferAccess && featureEnabled("loan_custom_payment"),
    isPaymentByCardEnabled:
      hasTransferAccess && featureEnabled("loan_payment_by_card"),
  });
};

export const useLoanPaymentForm = () => {
  const form = loanPaymentForm.useForm();
  const { values } = form;
  const {
    toAccountId: selectedToAccountId,
    paymentType: selectedPaymentType,
    fundingMethod: selectedFundingMethod,
  } = values;
  const isFixedAmount = selectedPaymentType === "amountDue";

  return useObjectMemo({
    form,
    /** Flags for determining which elements of the form should appear */
    formMetadata: {
      isFixedAmount,
      isSelectPaymentTypeVisible: !!selectedToAccountId,
      isPaymentDetailsVisible: !!selectedPaymentType,
      isPaymentDetailsPrincipalVisible: selectedPaymentType === "principal",
      isPaymentDetailsCardVisible:
        selectedFundingMethod === "card" && selectedPaymentType !== "principal",
      isPaymentDetailsBankVisible:
        selectedFundingMethod === "bank" && selectedPaymentType !== "principal",
    },
  });
};

/**
 * @deprecated Refer to `useLoanPaymentEnabled` for loan related permissions
 */
export const useLoanCanPayPrincipalOnly = (accountId: API.AccountId) => {
  const { featureEnabled } = useFeature();

  const isValidLoan = useSelector((state: RootState) => {
    const account = selectBAccount(state, accountId);
    return isLoan(account);
  });

  return (
    featureEnabled({
      and: ["internal_transfers", "loan_principal_payment"],
    }) && isValidLoan
  );
};

export const useLoanMinimumPayment = (accountId: API.AccountId) => {
  const minimumPayment = useSelector((state: RootState) => {
    if (accountId) {
      return selectMinimumPayment(state, accountId) || (0 as Cents);
    }
    return 0 as Cents;
  });

  const dueDate = useSelector((state: RootState) => {
    return selectMinPaymentDueDate(state, accountId) || "";
  });

  const institution = useSelector(selectInstitution);

  const formattedDueDate = useMemo(
    () => (dueDate ? filters.shortDate(dueDate) : ""),
    [dueDate],
  );

  const isPaymentPastDue = useMemo(() => {
    if (!dueDate) return false;
    if (minimumPayment <= 0) return false;

    const overdueThreshold = addDays(
      new Date(dueDate),
      institution?.loan_payment_overdue_days || 1,
    );
    return !isFuture(overdueThreshold);
  }, [dueDate, minimumPayment, institution?.loan_payment_overdue_days]);

  return useObjectMemo({
    minimumPayment,
    formattedDueDate,
    isPaymentPastDue,
    formattedMinimumPayment: filters.currency(minimumPayment),
  });
};
